import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import es from "./locales/es.json";
import en from "./locales/en.json";
import ca from "./locales/ca.json";
import eu from "./locales/eu.json";
import gl from "./locales/gl.json";
import va from "./locales/va.json";

/**
 * Idiomas de la web pública: castellano (fuente) + las cuatro lenguas
 * cooficiales de España + inglés. El orden aquí es el orden del selector.
 * `es` es siempre el fallback: cualquier clave sin traducir cae al castellano.
 */
export const SUPPORTED_LANGS = ["es", "ca", "va", "gl", "eu", "en"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

/** Autónimos para el selector (cada idioma en su propia lengua). */
export const LANG_LABELS: Record<Lang, string> = {
  es: "Español",
  ca: "Català",
  va: "Valencià",
  gl: "Galego",
  eu: "Euskara",
  en: "English",
};

/** Etiqueta corta para el chip del selector. */
export const LANG_SHORT: Record<Lang, string> = {
  es: "ES",
  ca: "CA",
  va: "VA",
  gl: "GL",
  eu: "EU",
  en: "EN",
};

/** Tag BCP-47 por idioma, para formateo de fechas/números con Intl. */
const INTL_LOCALE: Record<Lang, string> = {
  es: "es-ES",
  ca: "ca-ES",
  va: "ca-ES-valencia",
  gl: "gl-ES",
  eu: "eu-ES",
  en: "en-GB",
};

/**
 * Detección por navegador solo para las lenguas cooficiales: un navegador en
 * catalán, valenciano, gallego o euskera abre la web en su idioma; cualquier
 * otro (inglés incluido) la abre en castellano, que es la versión que indexa
 * Google (su renderizador usa un navegador en-US y, con la detección normal,
 * veía los títulos en inglés). El inglés sigue disponible en el selector.
 */
const AUTO_LANGS = ["ca", "va", "gl", "eu"];
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: "coofficialNavigator",
  lookup() {
    if (typeof navigator === "undefined") return undefined;
    const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const base = (l ?? "").toLowerCase().split("-")[0];
      if (base === "es") return "es";
      if (AUTO_LANGS.includes(base)) return base;
    }
    return undefined;
  },
});

void i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      es: { translation: es },
      ca: { translation: ca },
      va: { translation: va },
      gl: { translation: gl },
      eu: { translation: eu },
      en: { translation: en },
    },
    fallbackLng: "es",
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    // `en-US` → `en`, `ca-ES` → `ca`: nos quedamos con la parte de idioma.
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    detection: {
      order: ["localStorage", "coofficialNavigator"],
      lookupLocalStorage: "recine_lang",
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

/** Normaliza cualquier lng detectado a uno de los soportados (o `es`). */
export function normalizeLang(lng: string | undefined): Lang {
  const base = (lng?.split("-")[0] ?? "es") as Lang;
  return (SUPPORTED_LANGS as readonly string[]).includes(base) ? base : "es";
}

// Mantén <html lang> sincronizado con el idioma activo (SEO + a11y).
const applyHtmlLang = (lng: string) => {
  document.documentElement.lang = normalizeLang(lng);
};
applyHtmlLang(i18n.language || "es");
i18n.on("languageChanged", applyHtmlLang);

/** Locale BCP-47 del idioma activo, para `toLocaleDateString` y afines. */
export function currentIntlLocale(): string {
  return INTL_LOCALE[normalizeLang(i18n.language)];
}

export default i18n;
